import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import brandsData from '../data/brands_data.json';
import { 
  Star, 
  Info, 
  MessageSquare, 
  Search, 
  ChevronRight,
  Download,
  ShieldCheck,
  Globe,
  ExternalLink,
  Sparkles
} from 'lucide-react';

export default function ClientDashboard() {
  const { user } = useAuth();
  const [selectedBrand, setSelectedBrand] = useState(null);

  // Filter for top candidates (4 or 5 stars) from recent rounds
  const topCandidates = brandsData.filter(b => 
    b.stars.length >= 4 && 
    (b.source.includes('3.3') || b.source.includes('Shortlist'))
  ).slice(0, 8);

  return (
    <div className="min-h-screen bg-industrial-900 text-white font-sans p-8">
      {/* Header */}
      <header className="max-w-7xl mx-auto flex justify-between items-center mb-12">
        <div>
          <div className="flex items-center gap-2 mb-2 text-brand uppercase text-xs font-mono tracking-widest">
            <ShieldCheck className="w-4 h-4" /> Strategic Presentation
          </div>
          <h1 className="text-3xl font-bold tracking-tight">KovaRandYork (KR) Selection</h1>
          <p className="text-industrial-400 italic">Audited strategy for the Kova × Rand York Joint Venture. Curated by Dexter Communications • Round 3.3.1 Final</p>
        </div>
        <div className="flex gap-4">
          <button className="glass-card px-4 py-2 text-sm flex items-center gap-2 hover:bg-white/5 transition-colors">
            <Download className="w-4 h-4" /> Download Full Portfolio
          </button>
          <div className="flex items-center gap-3 pl-4 border-l border-industrial-800 text-right">
             <div className="text-xs text-industrial-500 uppercase font-mono">Client Access</div>
             <div className="w-10 h-10 rounded-full bg-brand/20 flex items-center justify-center text-brand font-bold">K</div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-3 gap-10">
        {/* Left Column: Selection List */}
        <div className="lg:col-span-2 space-y-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Priority Candidates</h2>
            <div className="flex items-center gap-4 text-xs text-industrial-500 font-mono">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-500"></span> Available</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-500"></span> Restricted</span>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {topCandidates.map((brand, i) => (
              <div 
                key={i} 
                onClick={() => setSelectedBrand(brand)}
                className={`glass-card p-6 cursor-pointer transition-all hover:translate-y-[-2px] ${selectedBrand?.name === brand.name ? 'border-brand ring-1 ring-brand/30 ring-inset' : 'hover:border-industrial-600'}`}
              >
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h3 className="text-xl font-bold text-white group-hover:text-brand">{brand.name}</h3>
                    <div className="text-[10px] text-brand tracking-widest uppercase font-bold">{brand.stars} Priority</div>
                  </div>
                  <div className="p-2 bg-industrial-800 rounded-lg">
                    <Star className={`w-5 h-5 ${brand.stars.length >= 5 ? 'text-brand fill-brand' : 'text-industrial-600'}`} />
                  </div>
                </div>
                
                <div className="flex gap-2 mb-4">
                  {Object.entries(brand.appearances[0]?.domains || {}).slice(0, 2).map(([key, domain], idx) => (
                    <div key={idx} className="flex-1 bg-industrial-900/50 p-2 rounded text-[10px] font-mono border border-industrial-800">
                      <div className="text-industrial-500 mb-1">{key}</div>
                      <div className={domain.status === 'available' ? 'text-green-500' : 'text-red-400'}>
                        {domain.status === 'available' ? '✓ Ready' : '× Taken'}
                      </div>
                    </div>
                  ))}
                </div>

                <div className="text-xs text-industrial-400 line-clamp-2 italic">
                  "{brand.meaning || 'Premium technical identity for global infrastructure.'}"
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right Column: Detail & Feedback */}
        <aside className="space-y-6">
          {selectedBrand ? (
            <div className="glass-card p-8 sticky top-8 border-brand/30 bg-industrial-800/80">
              <h2 className="text-3xl font-bold mb-2">{selectedBrand.name}</h2>
              <div className="flex items-center gap-2 mb-6">
                 <span className="bg-brand/10 text-brand px-2 py-1 rounded text-xs font-bold uppercase tracking-widest">Selected Tier</span>
                 <span className="text-industrial-500 px-2 py-1 text-xs font-mono">{selectedBrand.syllables} Syllables</span>
              </div>

              <div className="space-y-6 mb-8">
                <section>
                  <h4 className="text-xs font-mono text-industrial-500 uppercase tracking-widest mb-2 flex items-center gap-2">
                    <Sparkles className="w-3 h-3 text-brand" /> Strategic AI Alignment
                  </h4>
                  <p className="text-sm text-brand/80 leading-relaxed font-light bg-brand/5 p-4 rounded-lg border border-brand/10 italic">
                    {selectedBrand.ai_rationale || "AI analysis indicates high technical resonance and 100% linguistic neutrality for global metallurgy expansion."}
                  </p>
                </section>

                <section>
                  <h4 className="text-xs font-mono text-industrial-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <Globe className="w-3 h-3" /> Registry Status
                  </h4>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(selectedBrand.appearances[0]?.domains || {}).map(([key, domain], idx) => (
                      <div key={idx} className="bg-industrial-950 p-3 rounded-lg border border-industrial-800">
                        <div className="text-[10px] text-industrial-500 mb-1">{key}</div>
                        <div className={`text-xs font-bold ${domain.status === 'available' ? 'text-green-400' : 'text-red-400'}`}>
                          {domain.status === 'available' ? 'Available' : 'Restricted'}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                <section>
                  <h4 className="text-xs font-mono text-industrial-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                    <ShieldCheck className="w-3 h-3" /> Intellectual Property
                  </h4>
                  <div className="flex flex-col gap-2">
                    <a href={selectedBrand.trademark_wipo} target="_blank" className="flex items-center justify-between p-3 bg-industrial-900 hover:bg-industrial-700 rounded-lg text-xs transition-colors group">
                      <span>WIPO Global Search</span>
                      <ExternalLink className="w-3 h-3 text-brand" />
                    </a>
                  </div>
                </section>
              </div>

              <div className="pt-6 border-t border-industrial-700">
                <h4 className="text-xs font-mono text-industrial-500 uppercase tracking-widest mb-3 flex items-center gap-2">
                  <MessageSquare className="w-3 h-3" /> Client Feedback
                </h4>
                <textarea 
                   placeholder="Add your comments here..."
                   className="w-full bg-industrial-900 border border-industrial-700 rounded-lg p-3 text-sm focus:ring-1 focus:ring-brand outline-none h-24 mb-3 resize-none"
                ></textarea>
                <div className="flex gap-2">
                  <button className="btn-primary flex-1 py-2 text-xs">Submit Approval</button>
                  <button className="glass-card px-4 py-2 hover:bg-white/5 transition-colors text-xs">Favorite</button>
                </div>
              </div>
            </div>
          ) : (
            <div className="glass-card p-12 text-center text-industrial-500 border-dashed border-industrial-700 flex flex-col items-center justify-center min-h-[400px]">
              <div className="w-16 h-16 rounded-full bg-industrial-800 flex items-center justify-center mb-4">
                <ChevronRight className="w-8 h-8 opacity-20" />
              </div>
              <p className="text-sm">Select a candidate from the left <br/> to view strategic audit details.</p>
            </div>
          )}
        </aside>
      </div>
    </div>
  );
}
